import { Fragment, useEffect, useState } from 'react';
import {
  fetchProductMapsByRpmAcctProductId,
  fetchProductMapsBySFDCProductId,
  getProductMapsByRpmAcctProductIdLoadingSubject,
  getProductMapsByRpmAcctProductIdSubject,
  getProductMapsBySFDCProductIdLoadingSubject,
  getProductMapsBySFDCProductIdSubject,
  ProductMap,
  updateProductMap
} from '../../../../../store/ProductMap';
import { DetailPanelParams } from '../../../../Compounds/DetailPanel/DetailPanel';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle';
import { DetailPanelContainer } from '../../../../Compounds/DetailPanel/DetailPanelContainer';
import { EditFieldType } from '../../../../Compounds/DetailPanel/LabelValueField';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { MapMiniTable } from '../../../../Compounds/Mapping/MiniTable/MapMiniTable';
import { sfdcTableCols } from './SFDCColumnDef';
import { rpmTableCols } from './RPMColumnDef';
import { fetchProducts, getProductsSubject, Product } from '../../../../../store/Product';

export interface ProductMapDetailsParams {
  data: ProductMap;
}

export const pMapSections = (data: ProductMap): DetailPanelParams[] => [
  {
    data,
    canEdit: [{ field: 'reviewRequired', type: EditFieldType.CHECKBOX }],
    fieldMap: {
      productFamily: 'Salesforce Product Family',
      productName: 'Salesforce Product Name',
      productSfId: 'Salesforce ID',
      reviewRequired: 'Review Required',
      rpmProductSupplierName: 'RPM Supplier',
      rpmProductName: 'RPM Product',
      rpmProductId: 'RPM ID'
    },
    order: [
      'productFamily',
      'rpmProductSupplierName',
      'productName',
      'rpmProductName',
      'productSfId',
      'rpmProductId',
      'reviewRequired'
    ],
    links: [
      {
        field: 'productName',
        link: null,
        externalLink: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${data.productSfId}/view`,
        external: true
      },
      {
        field: 'rpmProductSupplierName',
        link: null,
        externalLink: `${process.env.REACT_APP_RPM_URL}/rpm/Page/Supplier.aspx?Item=${data.rpmProductSupplierId}`,
        external: true
      },
      {
        field: 'rpmProductName',
        link: null,
        externalLink: `${process.env.REACT_APP_RPM_URL}/rpm/Page/Product.aspx?Item=${data.rpmProductId}`,
        external: true
      }
    ]
  },
  {
    data,
    canEdit: [],
    fieldMap: {
      createdBy: 'Added By',
      createdAt: 'Added Date'
    },
    associationNameFields: {
      createdBy: 'createdBy'
    },
    order: ['createdBy', 'createdAt']
  },
  {
    data,
    canEdit: [{ field: 'note', type: EditFieldType.TEXTAREA }],
    fieldMap: {
      note: 'Note'
    },
    order: ['note']
  }
];

export const ProductMapDetails = ({ data }: ProductMapDetailsParams) => {
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [presetString, setPresetString] = useState<string>('');
  const [mapFetchId, setMapFetchId] = useState<string>('');
  const [mapFetchFunc, setMapFetchFunc] = useState<(id: string) => Promise<ProductMap[] | undefined>>();
  const [preFill, setPreFill] = useState<'SFDC' | 'RPM' | ''>('');
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  const handleSave = (update: ProductMap) => updateProductMap(update);

  const handleNewMap = (source: 'SFDC' | 'RPM') => {
    setPresetString(source === 'SFDC' ? data.productSfId : data.rpmProductId.toString());
    setPreFill(source);
    setIsMapFormOpen(true);
    setMapFetchFunc(
      source === 'SFDC' ? () => fetchProductMapsBySFDCProductId : () => fetchProductMapsByRpmAcctProductId
    );
    setMapFetchId(source === 'SFDC' ? data.productSfId : data.rpmProductId.toString());
  };

  useEffect(() => {
    fetchProducts();
    const productsSub = getProductsSubject().subscribe((products) => {
      setAllProducts(products);
    });
    return () => {
      productsSub.unsubscribe();
    };
  }, []);

  const cardActions = (source: 'SFDC' | 'RPM') => {
    return (
      <button
        className="flex items-center space-x-2"
        onClick={() => handleNewMap(source)}
        data-cy="new-product-map-button"
        data-testid="new-product-map-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Product Map
        </TextSpan>
      </button>
    );
  };

  return (
    <Fragment>
      <div
        className="space-y-4"
        data-testid="product-map-details-drawer">
        <DetailViewCard
          enableCollapse
          key="product-map-detail-card"
          title="Info">
          <div
            data-testid="product-map-detail-card"
            className="min-h-[15rem]">
            <DetailPanelContainer
              data={pMapSections(data)}
              saveAction={(d) => handleSave(d as ProductMap)}
            />
          </div>
        </DetailViewCard>
        <DetailViewCard
          cardActions={cardActions('SFDC')}
          enableCollapse
          key="rpm_supplier_table"
          title="Also Mapped To Salesforce Product">
          <MapMiniTable
            tableCols={sfdcTableCols}
            id={data.productSfId || ''}
            exclude={[data.id || '']}
            fetchFunction={fetchProductMapsBySFDCProductId}
            dataSubject={getProductMapsBySFDCProductIdSubject}
            loadingSubject={getProductMapsBySFDCProductIdLoadingSubject}
          />
        </DetailViewCard>
        <DetailViewCard
          cardActions={cardActions('RPM')}
          enableCollapse
          key="SFDC_supplier_table"
          title="Also Mapped To RPM Product">
          <MapMiniTable
            tableCols={rpmTableCols}
            id={data.rpmProductId.toString() || ''}
            exclude={[data.id || '']}
            fetchFunction={fetchProductMapsByRpmAcctProductId}
            dataSubject={getProductMapsByRpmAcctProductIdSubject}
            loadingSubject={getProductMapsByRpmAcctProductIdLoadingSubject}
          />
        </DetailViewCard>
      </div>
      <NewMapDialog
        open={isMapFormOpen}
        setOpen={setIsMapFormOpen}
        mapType={EntityType.PRODUCT}
        inputString={presetString}
        preFill={preFill}
        fetch={mapFetchFunc}
        id={mapFetchId}
        allProducts={allProducts}
        productPrefill={{
          familyId: data.productFamily,
          productSfId: data.productSfId,
          rpmSupplierId: data.rpmProductSupplierId,
          rpmSupplierName: data.rpmProductSupplierName,
          rpmProductId: data.rpmProductId,
          rpmProductName: data.rpmProductName
        }}
      />
    </Fragment>
  );
};
